import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/InvestmentGuide.css';
import guideImg from '../assets/images/Investment-Guide-Intro.jpg';
import heroBg from '../assets/images/Investment-Guide-BG.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartSimple, faBuilding, faGlobe, faCoins } from '@fortawesome/free-solid-svg-icons';
import {
  faDollarSign,
  faHandHoldingDollar,
  faGem,
  faHouseLock,
  faEarthAsia,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import ScrollDownArrow from '../components/ScrollDownArrow';

const InvestmentGuide = () => {
  const infoBlockRefs = [useRef(null), useRef(null)];
  const navigate = useNavigate();
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );
    infoBlockRefs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => {
      infoBlockRefs.forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get form values
    const firstName = document.getElementById('firstName').value.trim();
    const lastName = document.getElementById('lastName').value.trim();
    const email = document.getElementById('email').value.trim();
    const contactNumber = document.getElementById('contactNumber').value.trim();
    const country = document.getElementById('country').value.trim();

    // Validation
    if (!firstName) {
      showToast('error', 'First name is required');
      return;
    }

    if (!lastName) {
      showToast('error', 'Last name is required');
      return;
    }

    if (!email) {
      showToast('error', 'Email is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast('error', 'Please enter a valid email address');
      return;
    }

    if (!contactNumber) {
      showToast('error', 'Contact number is required');
      return;
    }

    // Phone number validation (allows +, -, and numbers)
    const phoneRegex = /^[+]?[\d\s-]+$/;
    if (!phoneRegex.test(contactNumber)) {
      showToast('error', 'Please enter a valid contact number');
      return;
    }

    if (!country) {
      showToast('error', 'Country is required');
      return;
    }

    const formData = {
      firstName,
      lastName,
      email,
      contactNumber,
      country
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/investment-guide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        showToast(
          data.isExistingUser
            ? 'error'
            : 'success',
          data.isExistingUser
            ? 'User already exists. You have already requested the investment guide.'
            : 'Thank you for your interest! Your investment guide is ready.'
        );
        // Redirect after 3 seconds
        if (!data.isExistingUser) {
          setTimeout(() => {
            navigate('/investment-guide-success');
          }, 1000);
        }
      } else {
        showToast('error', data.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('error', 'Failed to submit form');
    }
  };

  // Helper function to show toast
  const showToast = (type, message) => {
    const toastDiv = document.createElement('div');
    toastDiv.className = `custom-alert ${type}`;
    toastDiv.innerHTML = `
      <div class="alert-content">
        <div class="icon">${type === 'error' ? '❌' : '✅'}</div>
        <div class="message">
          <h3>${type === 'error' ? 'Error' : 'Success'}</h3>
          <p>${message}</p>
        </div>
      </div>
    `;
    document.body.appendChild(toastDiv);
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toastDiv.classList.add('toast-exit');
      setTimeout(() => {
        document.body.removeChild(toastDiv);
      }, 300);
    }, 3000);
  };

  return (
    <div className="investment-guide-page">
      {/* Hero Section with CTA and Form */}
      <div
        className="investmentguide-hero"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="investmentguide-hero-overlay"></div>
        <div className="investmentguide-hero-content hero-content-with-form">
          <div className="hero-content-left">
            <h1 className="investmentguide-hero-title">Investment Guide</h1>
            <p className="investmentguide-hero-subtitle">
              Unlock Dubai's real estate potential with our expert investment guide and join the growing list of successful investors, 
              <br />
              <br />
              Join The Growing List Of Successful Investors.
            </p>
          </div>
          <div className="investmentguide-hero-form-card">
            <h3>Complete Your Details To View The Dubai Investment Guide</h3>
            <form onSubmit={handleSubmit} ref={formRef} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    minLength={2}
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    minLength={2}
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactNumber">Contact Number</label>
                <input
                  id="contactNumber"
                  type="tel"
                  placeholder="Eg: +971-xxx-xxx"
                />
              </div>
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select id="country" name="country">
                  <option value="">Select Country</option>
                  <option value="AUS">AUS</option>
                  <option value="UAE">UAE</option>
                  <option value="KSA">KSA</option>
                  <option value="Qatar">Qatar</option>
                  <option value="UK">UK</option>
                  <option value="India">India</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-row">
                <button type="submit" className="contact-btn">Submit</button>
              </div>
            </form>
          </div>
        </div>
        <ScrollDownArrow targetSelector=".investmentguide-main" />
      </div>

      {/* Main Content Section */}
      <section className="investmentguide-main">
        <div className="investmentguide-main-text">
          <h3>Let Your Investment Portfolio Thrive With Dubai's Real Estate</h3>
          <p>
            Join our comprehensive investment guide to unlock the potential of Dubai's thriving property market. Learn from industry experts and gain insider knowledge to make informed investment decisions.
          </p>
          <br />
          <p>
            Dubai's real estate market offers unprecedented opportunities for investors worldwide. With free-market reforms, world-class infrastructure, and a booming economy, now is the perfect time to enter this lucrative market. Our investment guide provides you with the essential insights, strategies, and expert knowledge needed to understand Dubai's property investment landscape.
          </p>
          <br />
          <p>
            Whether you're a first-time investor or looking to expand your portfolio, this investment guide is your roadmap to achieving your goals and making data-driven, strategic investment decisions in one of the world's most dynamic real estate markets.
          </p>
        </div>
        <div className="investmentguide-main-image">
          <img
            src={guideImg}
            alt="Investment Guide"
          />
        </div>
      </section>

      {/* Info Sections */}
      <section className="investmentguide-info-section">
        <div className="investmentguide-info-content">
          <div className="investmentguide-info-block" ref={infoBlockRefs[0]}>
            <h4>What You Will Learn</h4>
            <ul>
              <li>• The secrets to investing in Dubai's booming real estate market.</li>
              <li>• Why Dubai has safe and sustainable investment opportunities.</li>
              <li>• How to have insider off-plan projects in Dubai.</li>
              <li>• Proven strategies and step-by-step tips to help you with a successful property investment.</li>
              <li>• Our relationships with major developers in Dubai.</li>
              <li>• Typical property value in Downtown Dubai.</li>
              <li>• How to take advantage of quality Dubai investments.</li>
            </ul>
          </div>
          <div className="investmentguide-info-block" ref={infoBlockRefs[1]}>
            <h4>Benefits Of Attending</h4>
            <ul>
              <li><span>Exclusive Expertise</span><br /><p className='investmentguide-info-text'>Learn from seasoned professionals with years of Dubai real estate experience.</p></li>
              <li><span>Real-Life Examples</span><br /><p className='investmentguide-info-text'>Gain an understanding of property value in Dubai.</p></li>
              <li><span>Knowledge & Expertise</span><br /><p className='investmentguide-info-text'>Leave with the knowledge to make informed investment decisions.</p></li>
              <li><span>Clear Next Step</span><br /><p className='investmentguide-info-text'>Understand what's required to proceed with sustainable and profitable investments.</p></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Dubai Market Insights / Trends */}
      <section className="market-insights-section">
        <h2 className="section-title">Dubai Market Insights</h2>
        <div className="market-insights-cards">
          <div className="insight-card">
            <div className="insight-icon"><FontAwesomeIcon icon={faChartSimple} /></div>
            <div className="insight-value">7.5%</div>
            <div className="insight-label">Avg. Rental Yield</div>
          </div>
          <div className="insight-card">
            <div className="insight-icon"><FontAwesomeIcon icon={faBuilding} /></div>
            <div className="insight-value">+12%</div>
            <div className="insight-label">Annual Price Growth</div>
          </div>
          <div className="insight-card">
            <div className="insight-icon"><FontAwesomeIcon icon={faGlobe} /></div>
            <div className="insight-value">200+</div>
            <div className="insight-label">Nationalities Investing</div>
          </div>
          <div className="insight-card">
            <div className="insight-icon"><FontAwesomeIcon icon={faCoins} /></div>
            <div className="insight-value">No Tax</div>
            <div className="insight-label">On Property Income</div>
          </div>
        </div>
      </section>

      {/* Why Dubai Section */}
      <section className="why-dubai-section">
        <h2 className="section-title">Why Invest in Dubai?</h2>
        <div className="why-dubai-cards">
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faDollarSign} /></span><span className='why-label'>0% Property Tax</span></div>
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faHandHoldingDollar} /></span><span className='why-label'>High Rental Yields</span></div>
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faGem} /></span><span className='why-label'>World-Class Lifestyle</span></div>
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faHouseLock} /></span><span className='why-label'>Safe & Secure</span></div>
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faEarthAsia} /></span><span className='why-label'>Global Business Hub</span></div>
          <div className="why-card"><span className="why-icon"><FontAwesomeIcon icon={faIdCard} /></span><span className='why-label'>Easy Residency</span></div>
        </div>
      </section>

      
    </div>
  );
};

export default InvestmentGuide;
